import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PictureViewerComponent, TabletApp } from '@common';
import { getTaskMarkQuestion, toMark } from '@app/mark/utils';
import { MarkUtilsService } from '@app/mark/mark-utils.api.service';
import { MarkEnter, MarkMode, MarkOperateType, MarkParamsWithTask } from '@fuulea/mark-paper';
import { QuestionStatis, StudentScore } from '@shared/model/analysis.models';
import { TaskClassTypeEnum } from '@shared/model/task.models';
import { ItembankService } from '@shared/service/itembank.service';
import { SharedService } from '@shared/service/shared.service';
import { formatMarkedBy, getAnswerImageFileName } from '@shared/utils/utils';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';

/**
 * 作答复核查看器：顶部信息栏 + 通用图片查看器 + 左右切换箭头
 *
 * 切换序列、当前学生、标记等业务状态都收在这里，公共 picture-viewer 只负责图片展示。
 */
@Component({
  selector: 'fl-answer-review-viewer',
  imports: [NzButtonModule, NzIconModule, PictureViewerComponent],
  templateUrl: './answer-review-viewer.component.html',
  styleUrl: './answer-review-viewer.component.scss',
  host: { class: 'block size-full' },
})
export class AnswerReviewViewerComponent implements OnInit {
  readonly questionStatis = input.required<QuestionStatis>();
  readonly currentQuestionNo = input('');
  readonly taskTitle = input('');
  // 打开查看器的学生
  readonly answer = input.required<StudentScore>();
  // 进入查看器时作答详情列表的实际展示顺序，左右切换以它为准
  readonly sortedData = input<readonly StudentScore[]>([]);
  // 当前题目在题库里的 id，复核跳转时用于定位该题所在的拍照组
  readonly questionId = input<number>(null);
  // 任务信息，复核跳转需要
  readonly taskId = input<number>(null);
  readonly taskType = input<number>(null);
  readonly taskClassId = input<number | string>(null);

  readonly onClose = output<void>();
  // 标记变化后通知作答详情主表刷新
  readonly flagChange = output<StudentScore>();

  private itembankSrv = inject(ItembankService);
  private messageSrv = inject(NzMessageService);
  private router = inject(Router);
  private sharedSrv = inject(SharedService);
  private markUtilsSrv = inject(MarkUtilsService);

  // 可切换的作答学生，顺序与进入时的作答列表一致
  readonly students = signal<StudentScore[]>([]);
  readonly current = signal<StudentScore | null>(null);
  readonly photos = signal<string[]>([]);
  readonly collect = signal(false);
  readonly markData = signal('');
  readonly downloadFileName = signal('');
  readonly total = computed(() => this.students().length);
  readonly index = computed(() => {
    const current = this.current();
    return current ? this.students().findIndex(item => item.detailId === current.detailId) : -1;
  });
  /**
   * 是否显示复核按钮：非考试任务，且当前学生该题已阅
   *
   * 任务复核入口用的是整卷的 marked / hasSubjective / markableCount，作答详情接口没有返回这些字段，
   * 这里以该题阅卷状态（markedBy）为准：未阅为 null，阅卷中为「阅卷中」，有人阅过才显示。
   */
  readonly canReview = computed(() => {
    const current = this.current();
    if (!current || this.taskClassId() === TaskClassTypeEnum.EXAM) return false;
    const markedBy = formatMarkedBy(current.markedBy);
    return !!markedBy && markedBy !== '阅卷中';
  });

  ngOnInit() {
    // 列表行是作答详情表格 cloneDeep 出来的克隆，按 detailId 换回原始对象，打标后才能与主表同步
    const answersById = new Map(
      (this.questionStatis()?.answers ?? []).map(student => [student.detailId, student])
    );
    const students = this.sortedData()
      .filter(student => this.canViewAnswer(student))
      .map(student => answersById.get(student.detailId) ?? student);
    this.students.set(students);
    this.setStudent(students.find(student => student.detailId === this.answer().detailId) ?? this.answer());
  }

  close() {
    this.onClose.emit();
  }

  /**
   * 左右切换学生，到边界时提示
   */
  switchStudent(direction: 1 | -1) {
    const next = this.students()[this.index() + direction];
    if (!next) {
      this.messageSrv.warning(direction > 0 ? '后续无作答图片' : '前面无作答图片');
      return;
    }
    this.setStudent(next);
  }

  /**
   * 工具栏星标：给当前学生打标/取消，并通知背后主表刷新
   */
  async toggleCollect() {
    const current = this.current();
    if (!current) return;
    const nextFlag = current.flag ? 0 : 1;
    await this.itembankSrv.flagExerciseDetail(current.detailId, nextFlag);
    current.flag = nextFlag;
    this.collect.set(!!nextFlag);
    this.flagChange.emit(current);
  }

  /**
   * 复核：带着当前学生进入该题所在的复核阅卷页
   */
  async review() {
    const current = this.current();
    if (!current) return;
    // 需求要求新窗口打开：先同步开窗，避免 await 之后被浏览器拦截；平板 App 没有新窗口，直接走 toMark 的消息通道
    const win = TabletApp.isTabletApp() ? null : window.open('', '_blank');
    try {
      // 该题所在拍照组的题目 id，阅卷页据此初始选中该题
      const { questionIds } = await getTaskMarkQuestion(this.markUtilsSrv, this.taskId(), this.questionId());
      const params: MarkParamsWithTask = {
        taskId: this.taskId(),
        paperId: current.paperId,
        operateType: MarkOperateType.AUDIT,
        enter: MarkEnter.TASK,
        subjectId: this.sharedSrv.user.subject_id,
        mode: MarkMode.STUDENT,
        // 多模块任务下 id 可能是数组，取第一段练习记录
        paperExerciseId: Array.isArray(current.id) ? current.id[0] : current.id,
        userId: current.userId,
        taskType: this.taskType(),
        initQuestionIds: questionIds,
      };
      if (win) {
        win.location.href = this.router.serializeUrl(
          this.router.createUrlTree(['/mark'], { queryParams: { ...params, title: this.taskTitle() } })
        );
        return;
      }
      toMark(this.router, params, this.taskTitle());
    } catch {
      win?.close();
      this.messageSrv.error('进入复核失败，请重试');
    }
  }

  private canViewAnswer(student: StudentScore) {
    return !student.unfilled && (student.photos ?? []).some(photo => !photo.includes('**'));
  }

  private setStudent(student: StudentScore | null) {
    this.current.set(student);
    this.photos.set(student ? this.getViewerPhotos(student) : []);
    this.collect.set(!!student?.flag);
    this.markData.set(student?.markData ?? '');
    this.downloadFileName.set(student ? this.getDownloadFileName(student) : '');
  }

  private getViewerPhotos(student: StudentScore) {
    return (student.photos ?? []).filter(photo => !photo.includes('**'));
  }

  /**
   * 下载文件名：任务名称-学生姓名-第X题-分数.jpg，未给分时省略分数段
   */
  private getDownloadFileName(student: StudentScore) {
    return getAnswerImageFileName({
      taskName: this.taskTitle(),
      studentName: student.firstName,
      questionNo: this.currentQuestionNo(),
      score: student.score,
    });
  }
}
